import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authService } from '@/services/auth.service';
import { getAccessToken } from '@/lib/api-client';
import { getValidAccessToken, clearAuthCookies, refreshAccessToken, getRefreshToken, isTokenExpiringSoon } from '@/lib/auth-cookies';
import { useNavigate } from 'react-router-dom';
import type { User } from '@/types/api';

export function useAuth() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hasToken = typeof window !== 'undefined' && !!getAccessToken();

  // Get current user profile
  const {
    data: user,
    isLoading,
    error,
  } = useQuery<User, Error>({
    queryKey: ['user', 'profile'],
    queryFn: () => authService.getProfile(),
    enabled: hasToken,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Track if we're currently refreshing to avoid race conditions
  const isRefreshingRef = useRef(false);

  // Check token expiry periodically and try to refresh before logging out
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const checkTokenExpiry = async () => {
      // Skip if already refreshing
      if (isRefreshingRef.current) return;

      const validToken = getValidAccessToken();
      const rawToken = localStorage.getItem('saturn_access_token');
      
      // If we have a valid token, check if it's expiring soon and proactively refresh
      if (validToken && rawToken && isTokenExpiringSoon(rawToken)) {
        console.log('⏰ Token expiring soon, proactively refreshing...');
        isRefreshingRef.current = true;
        try {
          await refreshAccessToken();
        } finally {
          isRefreshingRef.current = false;
        }
        return;
      }
      
      // If we have a user session but no valid token, try to refresh
      if (user && !validToken) {
        const refreshToken = getRefreshToken();
        
        if (refreshToken) {
          console.log('🔄 Access token expired, attempting refresh...');
          isRefreshingRef.current = true;
          
          try {
            const success = await refreshAccessToken();
            
            if (success) {
              console.log('✅ Token refreshed successfully, session continues');
              // Invalidate user query to refetch with new token
              queryClient.invalidateQueries({ queryKey: ['user', 'profile'] });
              return;
            }
          } finally {
            isRefreshingRef.current = false;
          }
        }
        
        // If refresh failed or no refresh token, logout
        console.log('🔒 Token refresh failed, logging out...');
        clearAuthCookies();
        queryClient.clear();
        navigate('/auth/v2/login');
      }
    };

    // Check immediately
    checkTokenExpiry();

    // Then check every 30 seconds (more responsive than 1 minute)
    const interval = setInterval(checkTokenExpiry, 30 * 1000);

    return () => clearInterval(interval);
  }, [user, navigate, queryClient]);

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async ({
      provider,
      idToken,
    }: {
      provider: 'google';
      idToken: string;
    }) => {
      return authService.verify(provider, idToken);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['user', 'profile'], data.user);
      navigate('/dashboard/map');
    },
  });

  // Logout function
  const logout = () => {
    console.log('🔓 Logging out user...');
    
    // Clear tokens from localStorage and cookies
    clearAuthCookies();
    
    // Also call authService.logout() for any additional cleanup
    authService.logout();
    
    // Clear ALL React Query cache (including user data)
    queryClient.clear();
    
    console.log('✅ Logout complete, redirecting to login');
    
    // Redirect to login
    navigate('/auth/v2/login');
  };

  return {
    user,
    isLoading,
    isAuthenticated: !!user && !error,
    login: loginMutation.mutate,
    loginAsync: loginMutation.mutateAsync,
    isLoggingIn: loginMutation.isPending,
    loginError: loginMutation.error,
    logout,
  };
}

// Hook to check if user has specific permissions
export function usePermissions() {
  const { user } = useAuth();

  const hasPermission = (permission: keyof User['permissions']) => {
    return user?.permissions?.[permission] ?? false;
  };

  const hasAnyPermission = (
    permissions: Array<keyof User['permissions']>
  ) => {
    return permissions.some((p) => hasPermission(p));
  };

  const hasAllPermissions = (
    permissions: Array<keyof User['permissions']>
  ) => {
    return permissions.every((p) => hasPermission(p));
  };

  return {
    isOwner: hasPermission('owner'),
    canManageSchools: hasPermission('schoolManagement'),
    canManageSchedules: hasPermission('scheduleManagement'),
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
}

